import { ReactElement, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  AgencyInfoPage,
  CheckpointsPage,
  ClearanceLogsPage,
  CreateOperationPage,
  LandingPage,
  OfficialVerificationsPage,
  PublicRegistrationPage,
  MarketingRegistrationPage,
  SurePassAdminPage,
  UserDetailsPage,
  UserImportPage,
  UserManagementPage,
  ViewOperationPage,
  OperationActivityPage,
  OperationIncidentsPage,
  OperationIncidentPage,
} from '@pages';
import { PageLoader } from './components/PageLoader/PageLoader';
import './App.css';
import { useAuthContext } from './auth/auth0-provider-with-navigate';
import { useUser } from './hooks/useUser';
import { useInfo } from './hooks/useInfo';
import { createAuthAction } from './networkRequest/officialsService';
import InitialLogin from './auth/InitialLogin.tsx';
import { NotFoundFallback, Toast } from '@components';
import { ListOperationsPage } from '@pages';
import ProtectedRoute from './auth/ProtectedRoute.tsx';
import { isFeatureFlagEnabled } from './utils/featureFlagUtils.ts';
import { FEATURE_FLAGS, PAGE_PATHS, ROLES } from './constants.ts';

const App = (): ReactElement => {
  const { isLoading, error, isAuthenticated, loginWithRedirect } = useAuth0();
  const { user, loading: loadingUser, error: errorUser } = useUser();
  const { loading: loadingInfo } = useInfo();
  const [shouldRedirect, setShouldRedirect] = useState(true);
  const { loginNeedsRecording, setLoginNeedsRecording } = useAuthContext();
  const navigate = useNavigate();

  const location = useLocation();
  const currentPathname = location.pathname;
  const currentSearch = location.search;

  useEffect(() => {
    // relying on params in the URL because we cannot trust state variables due to navigating to/from the app
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const loginError = params.get('error');

    if (
      shouldRedirect &&
      !code &&
      !error &&
      loginError !== 'login_required' &&
      currentPathname !== PAGE_PATHS.register &&
      currentPathname !== '/leo'
    ) {
      loginWithRedirect({
        authorizationParams: {
          prompt: 'none',
        },
        appState: {
          silentAttempt: true,
          returnTo: currentPathname + currentSearch
        }
      });
    } else {
      setShouldRedirect(false);
    }
  }, [error, shouldRedirect, loginWithRedirect, currentPathname, currentSearch]);

  // add HelpScout Beacon
  useEffect(() => {
    const helpScoutInstallScript = document.createElement('script');
    helpScoutInstallScript.async = false;
    helpScoutInstallScript.textContent = '!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});';
    document.head.appendChild(helpScoutInstallScript);

    const helpScoutInitScript = document.createElement('script');
    helpScoutInitScript.async = false;
    helpScoutInitScript.setAttribute('data-testid', 'beacon-init');
    helpScoutInitScript.textContent = "window.Beacon('init', 'e387627c-b67d-497d-ad07-f93173566a08');";
    document.head.appendChild(helpScoutInitScript);

    return () => {
      document.head.removeChild(helpScoutInstallScript);
      document.head.removeChild(helpScoutInitScript);
    };
  }, []);

  useEffect(() => {
    const recordLogin = async () => {
      if (user) {
        const { data, error } = await createAuthAction(user.accessToken, user.profile.id, 'login');
        if (data) {
          // if the user has just logged in, redirect to the appropriate place
          if (user?.profile?.role?.name === ROLES.OFFICIAL) {
            navigate(PAGE_PATHS.operations)
          }

          if (user?.profile?.role?.name === ROLES.VERIFICATION_SPECIALIST || user?.profile?.role?.name === ROLES.AGENCY_VERIFIER) {
            navigate(PAGE_PATHS.verifications);
          }

          setLoginNeedsRecording(false);
        } else if (error) {
          console.error(error);
        }
      }
    };
    if (user && loginNeedsRecording) {
      recordLogin();
    }
  }, [user, loginNeedsRecording, setLoginNeedsRecording, navigate]);

  // add heap tracking to web app
  useEffect(() => {
    const heapInstallScript = document.createElement('script');
    heapInstallScript.async = false;
    heapInstallScript.textContent = `window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])}; heap.load("${import.meta.env.VITE_HEAP_CLIENT_ID}");`;
    document.head.appendChild(heapInstallScript);

    return () => {
      document.head.removeChild(heapInstallScript);
    }

  }, []);

  if (isLoading || loadingUser || loadingInfo || shouldRedirect || loginNeedsRecording) {
    return <PageLoader />;
  }

  if (currentPathname === PAGE_PATHS.register) {
    return (
      <Routes>
        <Route path={PAGE_PATHS.register} element={<PublicRegistrationPage />} />
      </Routes>
    );
  }

  if (currentPathname === '/leo') {
    return (
      <Routes>
        <Route path="/leo" element={<MarketingRegistrationPage />} />
      </Routes>
    );
  }

  if (
    currentPathname !== '/not-found' &&
    currentPathname !== '/login' &&
    !user &&
    !loadingUser &&
    !isLoading &&
    !isAuthenticated
  ) {
    return <Navigate to="/login" />;
  }

  const isOperationsEnabled = user?.profile
    ? isFeatureFlagEnabled(user.profile, FEATURE_FLAGS.OPERATIONS)
    : false

  const isVerificationsEnabled = user?.profile
    ? isFeatureFlagEnabled(user.profile, FEATURE_FLAGS.VERIFICATIONS)
    : false

  const adminRouteRoles = [ROLES.ADMIN, ROLES.OWNER, ROLES.SUPER_ADMIN]

  return (
    <>
      <Routes>
        {(!user || !loadingUser || error || errorUser) && (
          <Route path="/login" element={<InitialLogin />} />
        )}
        {user && !loadingUser && (
          [
            <Route
              index
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles}>
                  <LandingPage />
                </ProtectedRoute>
              }
              key="/home"
            />,
            <Route
              path={PAGE_PATHS.users}
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles}>
                  <UserManagementPage />
                </ProtectedRoute>}
              key={PAGE_PATHS.users}
            />,
            <Route
              path={PAGE_PATHS.usersImport}
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles}>
                  <UserImportPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.usersImport}
            />,
            <Route
              path={`${PAGE_PATHS.users}/:officialId`}
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles} layoutGutterTop={false} layoutGutterHorizontal={false}>
                  <UserDetailsPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.users}/:officialId`}
            />,
            <Route
              path={PAGE_PATHS.checkpoints}
              element={
                <ProtectedRoute
                  user={user}
                  permittedRoles={adminRouteRoles}
                  layoutGutterTop={false}
                  layoutGutterHorizontal={false}
                  layoutGutterBottom={false}
                >
                  <CheckpointsPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.checkpoints}
            />,
            <Route
              path={PAGE_PATHS.activity}
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles}>
                  <ClearanceLogsPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.activity}
            />,
            <Route
              path={PAGE_PATHS.operations}
              element={
                <ProtectedRoute
                  user={user}
                  permittedRoles={[...adminRouteRoles, ROLES.OFFICIAL]}
                  featureFlagEnabled={isOperationsEnabled}
                >
                  <ListOperationsPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.operations}
            />,
            <Route
              path={`${PAGE_PATHS.operations}/create`}
              element={
                <ProtectedRoute
                  user={user}
                  permittedRoles={[...adminRouteRoles]}
                  featureFlagEnabled={isOperationsEnabled}
                >
                  <CreateOperationPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.operations}/create`}
            />,
            <Route
              path={`${PAGE_PATHS.operations}/:id`}
              element={
                <ProtectedRoute user={user} permittedRoles={[...adminRouteRoles, ROLES.OFFICIAL]}>
                  <ViewOperationPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.operations}/:id`}
            />,
            <Route
              path={`${PAGE_PATHS.operations}/:id/activities`}
              element={
                <ProtectedRoute user={user} permittedRoles={[...adminRouteRoles, ROLES.OFFICIAL]}>
                  <OperationActivityPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.operations}/:id/activities`}
            />,
            <Route
              path={`${PAGE_PATHS.operations}/:operationId/incidents`}
              element={
                <ProtectedRoute user={user} permittedRoles={[...adminRouteRoles, ROLES.OFFICIAL]}>
                  <OperationIncidentsPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.operations}/:operationId/incidents`}
            />,
            <Route
              path={`${PAGE_PATHS.operations}/:operationId/incidents/:incidentId`}
              element={
                <ProtectedRoute user={user} permittedRoles={[...adminRouteRoles, ROLES.OFFICIAL]}>
                  <OperationIncidentPage />
                </ProtectedRoute>
              }
              key={`${PAGE_PATHS.operations}/:operationId/incidents/:incidentId`}
            />,
            <Route
              path={PAGE_PATHS.settings}
              element={
                <ProtectedRoute user={user} permittedRoles={adminRouteRoles}>
                  <AgencyInfoPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.settings}
            />,
            <Route
              path={PAGE_PATHS.surepassAdmin}
              element={
                <ProtectedRoute user={user} permittedRoles={[ROLES.SUPER_ADMIN]}>
                  <SurePassAdminPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.surepassAdmin}
            />,
            <Route
              path={PAGE_PATHS.verifications}
              element={
                <ProtectedRoute
                  user={user}
                  permittedRoles={[ROLES.VERIFICATION_SPECIALIST, ROLES.AGENCY_VERIFIER, ...adminRouteRoles]}
                  featureFlagEnabled={isVerificationsEnabled}
                >
                  <OfficialVerificationsPage />
                </ProtectedRoute>
              }
              key={PAGE_PATHS.verifications}
            />,
          ]
        )}
        <Route path="*" element={<NotFoundFallback />} />
      </Routes>
      {error && error.message !== 'Login required' && (
        <Toast type="error">{error.message}</Toast>
      )}
    </>
  );
};

export default App;
